import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import TipTapEditor from './TipTapEditor';
import api from '../services/api';
import { safeId, moneyFmt } from '../utils/helpers';

// Estilos para react-select (tema oscuro) - igual que CotizacionForm
const selectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.7)',
        borderColor: state.isFocused ? 'rgba(140, 220, 255, 0.5)' : 'rgba(140, 220, 255, 0.15)',
        borderRadius: '8px',
        minHeight: '38px',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(140, 220, 255, 0.3)' : 'none',
        '&:hover': { borderColor: 'rgba(140, 220, 255, 0.3)' }
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.98)',
        border: '1px solid rgba(140, 220, 255, 0.2)',
        borderRadius: '8px',
        zIndex: 100
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px 0',
        '::-webkit-scrollbar': { width: '6px' },
        '::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '3px' },
        '::-webkit-scrollbar-thumb': { background: 'rgba(140, 220, 255, 0.3)', borderRadius: '3px' },
        '::-webkit-scrollbar-thumb:hover': { background: 'rgba(140, 220, 255, 0.5)' }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? 'rgba(140, 220, 255, 0.2)' : state.isFocused ? 'rgba(140, 220, 255, 0.1)' : 'transparent',
        color: state.isSelected ? '#8cdcff' : '#e0e6ed',
        cursor: 'pointer',
        fontSize: '13px',
        padding: '8px 12px'
    }),
    groupHeading: (base) => ({
        ...base,
        color: '#8cdcff',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 4,
        paddingLeft: 12
    }),
    singleValue: (base) => ({ ...base, color: '#e0e6ed', fontSize: '14px' }),
    input: (base) => ({ ...base, color: '#e0e6ed', fontSize: '14px' }),
    placeholder: (base) => ({ ...base, color: 'rgba(224, 230, 237, 0.5)', fontSize: '14px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: 'rgba(140, 220, 255, 0.5)', '&:hover': { color: 'rgba(140, 220, 255, 0.8)' } }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

const PlantillaForm = ({ configuracion }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);
    const hasFetched = useRef(false);

    // Estado principal
    const [plantilla, setPlantilla] = useState({
        nombre: '',
        descripcion: '',
        activo: true
    });
    const [servicios, setServicios] = useState([]);
    const [extras, setExtras] = useState([]);
    const [servicioExpandido, setServicioExpandido] = useState(null);

    // Catálogos
    const [tiposServicioGrouped, setTiposServicioGrouped] = useState([]);
    const [personalList, setPersonalList] = useState([]);
    const [costosFijosList, setCostosFijosList] = useState([]);
    const [costosFijosGrouped, setCostosFijosGrouped] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Valores de configuración
    const rentabilidad = parseFloat(configuracion?.rentabilidad) || 0.68;
    const rentCostoFijo = parseFloat(configuracion?.rentabilidad_costo_fijo) || 0.68;

    // Cargar datos iniciales
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        cargarDatos();
    }, [id]);

    const cargarDatos = async () => {
        try {
            const [tiposRes, personalRes, cfRes, cfGroupedRes] = await Promise.all([
                api.tiposServicio.getGrouped(),
                api.personal.getAll(),
                api.costosFijos.getAll(),
                api.costosFijos.getGrouped()
            ]);

            setTiposServicioGrouped(tiposRes.data || []);
            setPersonalList(personalRes.data || []);
            setCostosFijosList(cfRes.data || []);
            setCostosFijosGrouped(cfGroupedRes.data || []);

            if (isEditing) {
                await cargarPlantilla();
            } else {
                // Nueva plantilla: agregar un servicio vacío
                const nuevoServicio = crearServicioVacio();
                setServicios([nuevoServicio]);
                setServicioExpandido(nuevoServicio._id);
                setLoading(false);
            }
        } catch (err) {
            console.error('Error cargando datos:', err);
            toast.error('Error al cargar datos');
            setLoading(false);
        }
    };

    const cargarPlantilla = async () => {
        try {
            const response = await api.plantillas.getById(id);
            const data = response.data;

            setPlantilla({
                nombre: data.nombre || '',
                descripcion: data.descripcion || '',
                activo: Boolean(data.activo)
            });

            const serviciosMapped = (data.servicios || []).map(s => ({
                _id: safeId(),
                id: s.id,
                tipo_servicio_id: s.tipo_servicio_id,
                tipo_servicio_nombre: s.tipo_servicio_nombre,
                detalle: s.detalle || '',
                asignaciones: (s.asignaciones || []).map(a => ({
                    _id: safeId(),
                    id: a.id,
                    personal_id: a.personal_id,
                    personal_nombre: a.personal_nombre,
                    horas: parseFloat(a.horas) || 0,
                    costo_hora: parseFloat(a.costo_hora) || 0,
                    rentabilidad: a.rentabilidad !== null ? parseFloat(a.rentabilidad) : rentabilidad
                })),
                costosFijos: (s.costosFijos || []).map(cf => ({
                    _id: safeId(),
                    id: cf.id,
                    costo_fijo_id: cf.costo_fijo_id,
                    costo_fijo_nombre: cf.costo_fijo_nombre,
                    categoria_nombre: cf.categoria_nombre,
                    costo: parseFloat(cf.costo) || 0,
                    rentabilidad: cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo
                }))
            }));

            if (serviciosMapped.length > 0) {
                setServicios(serviciosMapped);
                setServicioExpandido(serviciosMapped[0]._id);
            } else {
                const nuevoServicio = crearServicioVacio();
                setServicios([nuevoServicio]);
                setServicioExpandido(nuevoServicio._id);
            }

            setExtras((data.extras || []).map(e => ({
                _id: safeId(),
                id: e.id,
                detalle: e.detalle || '',
                monto: parseFloat(e.monto) || 0
            })));
        } catch (err) {
            console.error('Error cargando plantilla:', err);
            toast.error('Error al cargar plantilla');
            navigate('/plantillas');
        } finally {
            setLoading(false);
        }
    };

    const crearServicioVacio = () => ({
        _id: safeId(),
        tipo_servicio_id: null,
        tipo_servicio_nombre: '',
        detalle: '',
        asignaciones: [],
        costosFijos: []
    });

    // === Handlers de Servicios ===
    const agregarServicio = () => {
        const nuevo = crearServicioVacio();
        setServicios([...servicios, nuevo]);
        setServicioExpandido(nuevo._id);
    };

    const eliminarServicio = (servicioId) => {
        if (servicios.length <= 1) {
            toast.error('Debe haber al menos un servicio');
            return;
        }
        const nuevos = servicios.filter(s => s._id !== servicioId);
        setServicios(nuevos);
        if (servicioExpandido === servicioId && nuevos.length > 0) {
            setServicioExpandido(nuevos[0]._id);
        }
    };

    const actualizarServicio = (servicioId, campo, valor) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;

            if (campo === 'tipo_servicio_id') {
                const tipo = tiposServicioGrouped.flatMap(g => g.options).find(o => o.value === valor);
                return {
                    ...s,
                    tipo_servicio_id: valor,
                    tipo_servicio_nombre: tipo?.label || ''
                };
            }
            return { ...s, [campo]: valor };
        }));
    };

    // === Handlers de Asignaciones ===
    const agregarAsignacion = (servicioId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                asignaciones: [...s.asignaciones, {
                    _id: safeId(),
                    personal_id: null,
                    personal_nombre: '',
                    horas: 0,
                    costo_hora: 0,
                    rentabilidad: rentabilidad
                }]
            };
        }));
    };

    const eliminarAsignacion = (servicioId, asigId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                asignaciones: s.asignaciones.filter(a => a._id !== asigId)
            };
        }));
    };

    const actualizarAsignacion = (servicioId, asigId, campo, valor) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                asignaciones: s.asignaciones.map(a => {
                    if (a._id !== asigId) return a;

                    if (campo === 'personal_id' && valor) {
                        const personal = personalList.find(p => p.id === valor);
                        if (personal) {
                            return {
                                ...a,
                                personal_id: valor,
                                personal_nombre: personal.nombre,
                                costo_hora: parseFloat(personal.costo_hora) || 0
                            };
                        }
                    }
                    return { ...a, [campo]: campo === 'horas' || campo === 'rentabilidad' ? (parseFloat(valor) || 0) : valor };
                })
            };
        }));
    };

    // === Handlers de Costos Fijos ===
    const agregarCostoFijo = (servicioId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                costosFijos: [...s.costosFijos, {
                    _id: safeId(),
                    costo_fijo_id: null,
                    costo_fijo_nombre: '',
                    categoria_nombre: '',
                    costo: 0,
                    rentabilidad: rentCostoFijo
                }]
            };
        }));
    };

    const eliminarCostoFijo = (servicioId, cfId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                costosFijos: s.costosFijos.filter(cf => cf._id !== cfId)
            };
        }));
    };

    const actualizarCostoFijo = (servicioId, cfId, campo, valor) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                costosFijos: s.costosFijos.map(cf => {
                    if (cf._id !== cfId) return cf;

                    if (campo === 'costo_fijo_id' && valor) {
                        const costoFijo = costosFijosList.find(c => c.id === valor);
                        if (costoFijo) {
                            return {
                                ...cf,
                                costo_fijo_id: valor,
                                costo_fijo_nombre: costoFijo.nombre,
                                categoria_nombre: costoFijo.categoria_nombre || '',
                                costo: parseFloat(costoFijo.costo) || 0,
                                rentabilidad: costoFijo.rentabilidad !== null ? parseFloat(costoFijo.rentabilidad) : rentCostoFijo
                            };
                        }
                    }
                    return { ...cf, [campo]: campo === 'costo' || campo === 'rentabilidad' ? (parseFloat(valor) || 0) : valor };
                })
            };
        }));
    };

    // === Handlers de Extras ===
    const agregarExtra = () => {
        setExtras([...extras, { _id: safeId(), detalle: '', monto: 0 }]);
    };

    const eliminarExtra = (extraId) => {
        setExtras(extras.filter(e => e._id !== extraId));
    };

    const actualizarExtra = (extraId, campo, valor) => {
        setExtras(extras.map(e =>
            e._id === extraId ? { ...e, [campo]: campo === 'monto' ? (parseFloat(valor) || 0) : valor } : e
        ));
    };

    // === Guardar ===
    const handleGuardar = async () => {
        if (!plantilla.nombre.trim()) {
            toast.error('Ingrese un nombre para la plantilla');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...plantilla,
                servicios: servicios.map((s, idx) => ({
                    id: s.id,
                    tipo_servicio_id: s.tipo_servicio_id,
                    tipo_servicio_nombre: s.tipo_servicio_nombre,
                    detalle: s.detalle,
                    orden: idx,
                    asignaciones: s.asignaciones.map(a => ({
                        id: a.id,
                        personal_id: a.personal_id,
                        personal_nombre: a.personal_nombre,
                        horas: a.horas,
                        costo_hora: a.costo_hora,
                        rentabilidad: a.rentabilidad
                    })),
                    costosFijos: s.costosFijos.map(cf => ({
                        id: cf.id,
                        costo_fijo_id: cf.costo_fijo_id,
                        costo_fijo_nombre: cf.costo_fijo_nombre,
                        categoria_nombre: cf.categoria_nombre,
                        costo: cf.costo,
                        rentabilidad: cf.rentabilidad
                    }))
                })),
                extras: extras.map((e, idx) => ({
                    id: e.id,
                    detalle: e.detalle,
                    monto: e.monto,
                    orden: idx
                }))
            };

            if (isEditing) {
                await api.plantillas.update(id, payload);
                toast.success('Plantilla actualizada');
            } else {
                await api.plantillas.create(payload);
                toast.success('Plantilla creada');
            }
            navigate('/plantillas');
        } catch (err) {
            console.error('Error guardando plantilla:', err);
            toast.error('Error al guardar plantilla');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="loading">Cargando...</div>;
    }

    return (
        <div className="cotizacion-form">
            {/* Header */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">{isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}</h2>
                </div>

                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <label className="field" style={{ flex: 1, minWidth: '250px' }}>
                        <span className="label">Nombre de la plantilla *</span>
                        <input
                            className="input"
                            type="text"
                            value={plantilla.nombre}
                            onChange={(e) => setPlantilla({ ...plantilla, nombre: e.target.value })}
                            placeholder="Ej: Cotización estándar de video"
                        />
                    </label>
                    <div className="field">
                        <span className="label">Estado</span>
                        <div className="switch-container">
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={plantilla.activo}
                                    onChange={(e) => setPlantilla({ ...plantilla, activo: e.target.checked })}
                                />
                                <span className="switch-slider"></span>
                            </label>
                            <span className={`switch-label ${plantilla.activo ? 'active' : ''}`}>
                                {plantilla.activo ? 'Activo' : 'Inactivo'}
                            </span>
                        </div>
                    </div>
                </div>

                <label className="field" style={{ marginTop: '16px' }}>
                    <span className="label">Descripción (opcional)</span>
                    <textarea
                        className="input"
                        rows={2}
                        value={plantilla.descripcion}
                        onChange={(e) => setPlantilla({ ...plantilla, descripcion: e.target.value })}
                        placeholder="Breve descripción de cuándo usar esta plantilla"
                    />
                </label>
            </section>

            {/* Servicios */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">Servicios</h2>
                    <button className="btn neon" type="button" onClick={agregarServicio}>
                        + Agregar servicio
                    </button>
                </div>

                <div className="servicios-list">
                    {servicios.map((servicio, idx) => {
                        const isExpanded = servicioExpandido === servicio._id;
                        const toggleExpand = () => setServicioExpandido(isExpanded ? null : servicio._id);

                        return (
                            <div key={servicio._id} className={`servicio-item ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                <div className="servicio-header" onClick={toggleExpand}>
                                    <div className="servicio-header-left">
                                        <span className="servicio-number">#{idx + 1}</span>
                                        <div className="select-wrapper" onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isExpanded) setServicioExpandido(servicio._id);
                                        }}>
                                            <Select
                                                styles={selectStyles}
                                                options={tiposServicioGrouped}
                                                value={
                                                    tiposServicioGrouped
                                                        .flatMap(g => g.options)
                                                        .find(opt => opt.value === servicio.tipo_servicio_id) || null
                                                }
                                                onChange={(selected) => actualizarServicio(servicio._id, 'tipo_servicio_id', selected?.value || null)}
                                                placeholder="Seleccionar servicio..."
                                                isClearable
                                                isSearchable
                                                isDisabled={!isExpanded}
                                                noOptionsMessage={() => 'Sin opciones'}
                                            />
                                        </div>
                                    </div>
                                    <div className="servicio-header-right">
                                        <span className={`toggle-icon ${isExpanded ? 'rotated' : ''}`}>
                                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                                        </span>
                                        <button
                                            className="btn-icon delete"
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); eliminarServicio(servicio._id); }}
                                            title="Eliminar servicio"
                                        >
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div className={`servicio-content ${isExpanded ? 'open' : ''}`}>
                                    <div className="servicio-content-inner">
                                        <div className="servicio-detalle">
                                            <div className="field">
                                                <span className="label">Detalle del servicio</span>
                                                <TipTapEditor
                                                    value={servicio.detalle}
                                                    onChange={(html) => actualizarServicio(servicio._id, 'detalle', html)}
                                                    placeholder="Descripción detallada del servicio..."
                                                />
                                            </div>
                                        </div>

                                        {/* Sección de Costos de Servicio */}
                                        <div className="costos-servicio-section">
                                            <div className="costos-servicio-header">
                                                <span className="costos-servicio-title">COSTOS DE SERVICIO</span>
                                                <div className="costos-servicio-buttons">
                                                    <button
                                                        className="btn-add btn-add-personal"
                                                        type="button"
                                                        onClick={() => agregarAsignacion(servicio._id)}
                                                    >
                                                        <span className="btn-add-plus">+</span>
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                                        </svg>
                                                        Personal
                                                    </button>
                                                    <button
                                                        className="btn-add btn-add-costo-fijo"
                                                        type="button"
                                                        onClick={() => agregarCostoFijo(servicio._id)}
                                                    >
                                                        <span className="btn-add-plus">+</span>
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                                                        </svg>
                                                        Costo Fijo
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Divisor PERSONAL */}
                                            <div className="costos-divider personal-divider">
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                                </svg>
                                                <span>PERSONAL</span>
                                            </div>

                                            {/* Tabla de Personal */}
                                            {servicio.asignaciones.length > 0 && (
                                                <div className="asignaciones-table">
                                                    <div className="asignaciones-row asignaciones-header-row">
                                                        <div className="asig-col asig-personal">Personal</div>
                                                        <div className="asig-col asig-horas">Horas</div>
                                                        <div className="asig-col asig-costo">Costo/hr</div>
                                                        <div className="asig-col asig-rentabilidad">Rentab.</div>
                                                        <div className="asig-col asig-actions"></div>
                                                    </div>

                                                    {servicio.asignaciones.map((asig) => (
                                                        <div key={asig._id} className="asignaciones-row">
                                                            <div className="asig-col asig-personal">
                                                                <Select
                                                                    styles={selectStyles}
                                                                    options={personalList.map(p => ({
                                                                        value: p.id,
                                                                        label: `${p.nombre} (S/ ${p.costo_hora}/hr)`
                                                                    }))}
                                                                    value={
                                                                        asig.personal_id
                                                                            ? {
                                                                                value: asig.personal_id,
                                                                                label: (() => {
                                                                                    const p = personalList.find(p => p.id === asig.personal_id);
                                                                                    return p ? `${p.nombre} (S/ ${p.costo_hora}/hr)` : '';
                                                                                })()
                                                                            }
                                                                            : null
                                                                    }
                                                                    onChange={(selected) => actualizarAsignacion(servicio._id, asig._id, 'personal_id', selected?.value || null)}
                                                                    placeholder="Seleccionar..."
                                                                    isClearable
                                                                    isSearchable
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                />
                                                            </div>
                                                            <div className="asig-col asig-horas">
                                                                <input
                                                                    className="input input-sm"
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.5"
                                                                    value={asig.horas}
                                                                    onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'horas', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="asig-col asig-costo">
                                                                <input
                                                                    className="input input-sm"
                                                                    type="number"
                                                                    value={asig.costo_hora}
                                                                    disabled
                                                                />
                                                            </div>
                                                            <div className="asig-col asig-rentabilidad">
                                                                <div className="rentabilidad-input-wrapper">
                                                                    <input
                                                                        className="input input-sm input-rentabilidad"
                                                                        type="number"
                                                                        min="0"
                                                                        max="99"
                                                                        value={Math.round((asig.rentabilidad || rentabilidad) * 100)}
                                                                        onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'rentabilidad', (parseFloat(e.target.value) || 0) / 100)}
                                                                    />
                                                                    <span className="rentabilidad-percent">%</span>
                                                                </div>
                                                            </div>
                                                            <div className="asig-col asig-actions">
                                                                <button
                                                                    className="btn ghost small"
                                                                    type="button"
                                                                    onClick={() => eliminarAsignacion(servicio._id, asig._id)}
                                                                >
                                                                    x
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Divisor COSTOS FIJOS */}
                                            <div className="costos-divider costos-fijos-divider">
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                                                </svg>
                                                <span>COSTOS FIJOS</span>
                                            </div>

                                            {/* Tabla de Costos Fijos */}
                                            {servicio.costosFijos.length > 0 && (
                                                <div className="costos-fijos-table">
                                                    <div className="costos-fijos-row costos-fijos-header-row">
                                                        <div className="cf-col cf-select">Costo Fijo</div>
                                                        <div className="cf-col cf-costo">Costo</div>
                                                        <div className="cf-col cf-rentabilidad">Rentab.</div>
                                                        <div className="cf-col cf-actions"></div>
                                                    </div>

                                                    {servicio.costosFijos.map((cf) => (
                                                        <div key={cf._id} className="costos-fijos-row">
                                                            <div className="cf-col cf-select">
                                                                <Select
                                                                    styles={selectStyles}
                                                                    options={costosFijosGrouped}
                                                                    value={
                                                                        cf.costo_fijo_id
                                                                            ? costosFijosGrouped
                                                                                .flatMap(g => g.options)
                                                                                .find(opt => opt.value === cf.costo_fijo_id) || { value: cf.costo_fijo_id, label: cf.costo_fijo_nombre }
                                                                            : null
                                                                    }
                                                                    onChange={(selected) => actualizarCostoFijo(servicio._id, cf._id, 'costo_fijo_id', selected?.value || null)}
                                                                    placeholder="Seleccionar costo fijo..."
                                                                    isSearchable
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                />
                                                            </div>
                                                            <div className="cf-col cf-costo">
                                                                <input
                                                                    className="input input-sm"
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={cf.costo}
                                                                    onChange={(e) => actualizarCostoFijo(servicio._id, cf._id, 'costo', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="cf-col cf-rentabilidad">
                                                                <div className="rentabilidad-input-wrapper">
                                                                    <input
                                                                        className="input input-sm input-rentabilidad"
                                                                        type="number"
                                                                        min="0"
                                                                        max="99"
                                                                        value={Math.round((cf.rentabilidad || rentCostoFijo) * 100)}
                                                                        onChange={(e) => actualizarCostoFijo(servicio._id, cf._id, 'rentabilidad', (parseFloat(e.target.value) || 0) / 100)}
                                                                    />
                                                                    <span className="rentabilidad-percent">%</span>
                                                                </div>
                                                            </div>
                                                            <div className="cf-col cf-actions">
                                                                <button
                                                                    className="btn ghost small"
                                                                    type="button"
                                                                    onClick={() => eliminarCostoFijo(servicio._id, cf._id)}
                                                                >
                                                                    x
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Extras */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">Extras (opcional)</h2>
                    <button className="btn ghost" type="button" onClick={agregarExtra}>
                        + Agregar extra
                    </button>
                </div>

                <div className="extras-list">
                    {extras.length === 0 ? (
                        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '24px' }}>
                            Sin extras definidos
                        </p>
                    ) : (
                        extras.map((extra) => (
                            <div key={extra._id} className="extra-item">
                                <div className="extra-content">
                                    <div className="extra-detalle">
                                        <input
                                            className="input"
                                            type="text"
                                            value={extra.detalle}
                                            onChange={(e) => actualizarExtra(extra._id, 'detalle', e.target.value)}
                                            placeholder="Detalle del extra..."
                                        />
                                    </div>
                                    <div className="extra-costo">
                                        <span className="label">Monto</span>
                                        <input
                                            className="input"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={extra.monto}
                                            onChange={(e) => actualizarExtra(extra._id, 'monto', e.target.value)}
                                        />
                                    </div>
                                    <button
                                        className="btn-icon delete"
                                        type="button"
                                        onClick={() => eliminarExtra(extra._id)}
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </section>

            {/* Botones de acción */}
            <div className="form-actions-bar">
                <button className="btn ghost" type="button" onClick={() => navigate('/plantillas')}>
                    Cancelar
                </button>
                <button className="btn neon" type="button" onClick={handleGuardar} disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar Plantilla'}
                </button>
            </div>
        </div>
    );
};

export default PlantillaForm;
